import React, { useEffect, useState } from 'react';
import { AppUser, Empresa, Sitio } from '../types';
import {
  assignUserToEmpresa,
  createEmpresa,
  deleteEmpresa,
  getEmpresas,
  unassignUserFromEmpresa,
  updateEmpresa,
} from '../services/empresas.service';
import { getSitios } from '../services/sitios.service';
import { getUsers } from '../services/users.service';
import { getErrorMessage } from '../utils/errorHandler';

const EmpresasPage: React.FC = () => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [sitios, setSitios] = useState<Sitio[]>([]);
  const [users, setUsers] = useState<AppUser[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [editId, setEditId] = useState<string | null>(null);
  const [cuit, setCuit] = useState('');
  const [razonSocial, setRazonSocial] = useState('');
  const [domicilio, setDomicilio] = useState('');
  const [activo, setActivo] = useState(true);
  const [sucursales, setSucursales] = useState<string[]>([]);

  const [empresaRelacionId, setEmpresaRelacionId] = useState('');
  const [userRelacionId, setUserRelacionId] = useState('');

  const cargar = async () => {
    setLoading(true);
    try {
      const [empresasResp, sitiosResp, usersResp] = await Promise.all([getEmpresas(), getSitios(), getUsers()]);
      setEmpresas(empresasResp.data);
      setSitios(sitiosResp.data);
      setUsers(usersResp.data);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void cargar();
  }, []);

  const resetForm = () => {
    setEditId(null);
    setCuit('');
    setRazonSocial('');
    setDomicilio('');
    setActivo(true);
    setSucursales([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        cuit,
        razonSocial,
        domicilio,
        activo,
        sucursales,
      };
      if (editId) {
        await updateEmpresa(editId, payload);
      } else {
        await createEmpresa(payload);
      }
      resetForm();
      await cargar();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (empresa: Empresa) => {
    setEditId(empresa._id);
    setCuit(empresa.cuit);
    setRazonSocial(empresa.razonSocial);
    setDomicilio(empresa.domicilio);
    setActivo(empresa.activo);
    setSucursales(
      (empresa.sucursales || []).map((sucursal) => (typeof sucursal === 'string' ? sucursal : sucursal._id)),
    );
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar esta empresa?')) return;
    setError('');
    try {
      await deleteEmpresa(id);
      await cargar();
    } catch (err: any) {
      setError(getErrorMessage(err));
    }
  };

  const handleAsignarUsuario = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!empresaRelacionId || !userRelacionId) return;
    setSaving(true);
    setError('');
    try {
      await assignUserToEmpresa(empresaRelacionId, userRelacionId);
      setUserRelacionId('');
      await cargar();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDesasignarUsuario = async (empresaId: string, userId: string) => {
    setError('');
    try {
      await unassignUserFromEmpresa(empresaId, userId);
      await cargar();
    } catch (err: any) {
      setError(getErrorMessage(err));
    }
  };

  const usersByEmpresa = (empresaId: string) => users.filter((u) => u.empresaId === empresaId);

  return (
    <>
      <h1 className="page-title">Empresas / Clientes</h1>

      <div className="card">
        <h2 className="card-title">{editId ? 'Editar empresa' : 'Nueva empresa'}</h2>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>CUIT</label>
              <input value={cuit} onChange={(e) => setCuit(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Razon social</label>
              <input value={razonSocial} onChange={(e) => setRazonSocial(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Domicilio</label>
              <input value={domicilio} onChange={(e) => setDomicilio(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Sucursales (sitios)</label>
              <select
                multiple
                value={sucursales}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map((option) => option.value);
                  setSucursales(selected);
                }}
              >
                {sitios.map((sitio) => (
                  <option key={sitio._id} value={sitio._id}>
                    {sitio.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="checkbox-group">
            <input type="checkbox" id="empresaActiva" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
            <label htmlFor="empresaActiva">Empresa activa</label>
          </div>

          <div className="actions-cell">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Guardando...' : editId ? 'Guardar cambios' : 'Crear empresa'}
            </button>
            {editId && (
              <button type="button" className="btn btn-outline" onClick={resetForm}>
                Cancelar edición
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Relacionar empresa con usuarios</h2>
        <form onSubmit={handleAsignarUsuario}>
          <div className="form-grid">
            <div className="form-group">
              <label>Empresa</label>
              <select value={empresaRelacionId} onChange={(e) => setEmpresaRelacionId(e.target.value)} required>
                <option value="">Seleccionar empresa...</option>
                {empresas.map((empresa) => (
                  <option key={empresa._id} value={empresa._id}>
                    {empresa.razonSocial}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Usuario</label>
              <select value={userRelacionId} onChange={(e) => setUserRelacionId(e.target.value)} required>
                <option value="">Seleccionar usuario...</option>
                {users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.nombre} ({user.email})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Asignando...' : 'Asignar usuario a empresa'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2 className="card-title">Listado de empresas</h2>
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : empresas.length === 0 ? (
          <div className="empty-state">No hay empresas registradas.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Razon social</th>
                  <th>CUIT</th>
                  <th>Domicilio</th>
                  <th>Sucursales</th>
                  <th>Usuarios</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empresas.map((empresa) => (
                  <tr key={empresa._id}>
                    <td>{empresa.razonSocial}</td>
                    <td>{empresa.cuit}</td>
                    <td>{empresa.domicilio}</td>
                    <td>
                      {empresa.sucursales?.length
                        ? empresa.sucursales
                            .map((sucursal) => (typeof sucursal === 'string' ? sucursal : sucursal.nombre))
                            .join(' | ')
                        : '-'}
                    </td>
                    <td>
                      {usersByEmpresa(empresa._id).length ? (
                        usersByEmpresa(empresa._id).map((user) => (
                          <div key={user._id} style={{ marginBottom: '4px' }}>
                            {user.nombre}
                            <button
                              type="button"
                              className="btn btn-outline btn-sm"
                              style={{ marginLeft: '8px' }}
                              onClick={() => handleDesasignarUsuario(empresa._id, user._id)}
                            >
                              Quitar
                            </button>
                          </div>
                        ))
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`badge ${empresa.activo ? 'badge-success' : 'badge-secondary'}`}>
                        {empresa.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td>
                      <div className="actions-cell">
                        <button type="button" className="btn btn-outline btn-sm" onClick={() => handleEdit(empresa)}>
                          Editar
                        </button>
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(empresa._id)}>
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default EmpresasPage;
